import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, Phone } from "lucide-react";
import Logo from "./Logo";
import { COMPANY } from "../data/site";

const aproposLinks = [
  { label: "Notre histoire", to: "/a-propos" },
  { label: "FAQ générale", to: "/a-propos/faq-generale" },
];
const prestationsLinks = [
  { label: "Ménage", to: "/menage" },
  { label: "Conciergerie", to: "/conciergerie" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDrop, setOpenDrop] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setOpenDrop(null);
  }, [location.pathname]);

  const dark = scrolled || mobileOpen;
  const textColor = dark ? "var(--ink)" : "#fff";

  return (
    <header
      className="nav-shell"
      data-testid="site-header"
      style={{
        background: dark ? "rgba(255,255,255,0.96)" : "transparent",
        backdropFilter: dark ? "blur(14px)" : "none",
        boxShadow: dark ? "0 8px 30px -18px rgba(12,31,39,0.35)" : "none",
        padding: scrolled ? "0.7rem 0" : "1.4rem 0",
      }}
    >
      <div className="container-x flex items-center justify-between">
        <Link to="/" data-testid="logo-link" aria-label="Accueil Cleanova-Sud">
          <Logo light={!dark} size={scrolled ? 46 : 54} />
        </Link>

        <nav className="hidden lg:flex items-center gap-12 ml-10 whitespace-nowrap" style={{ color: textColor }}>
          <NavLink to="/" end className="nav-link" data-testid="nav-accueil">
            Accueil
          </NavLink>
          <NavLink to="/nos-locations" className="nav-link" data-testid="nav-locations">
            Nos locations
          </NavLink>
          <Dropdown
            label="Prestations"
            links={prestationsLinks}
            open={openDrop === "prestations"}
            onEnter={() => setOpenDrop("prestations")}
            onLeave={() => setOpenDrop(null)}
            testid="nav-prestations"
          />

          <Dropdown
            label="À propos"
            links={aproposLinks}
            open={openDrop === "apropos"}
            onEnter={() => setOpenDrop("apropos")}
            onLeave={() => setOpenDrop(null)}
            testid="nav-apropos"
          />

          <NavLink to="/contact" className="nav-link" data-testid="nav-contact">
            Contact
          </NavLink>
        </nav>

        <div className="hidden lg:flex items-center gap-4 ml-10">
          <a
            href={`tel:${COMPANY.phoneIntl}`}
            className="flex items-center gap-2 text-sm font-medium whitespace-nowrap"
            style={{ color: textColor }}
            data-testid="header-phone"
          >
            <Phone size={16} style={{ color: "var(--gold)" }} />
            {COMPANY.phone}
          </a>
          <Link to="/contact" className="btn btn-primary" data-testid="header-cta">
            Devis gratuit
          </Link>
        </div>

        <button
          className="lg:hidden"
          onClick={() => setMobileOpen((v) => !v)}
          aria-label="Menu"
          data-testid="mobile-menu-toggle"
          style={{ color: textColor }}
        >
          {mobileOpen ? <X size={28} /> : <Menu size={28} />}
        </button>
      </div>

      {mobileOpen && (
        <div
          className="lg:hidden dropdown-panel"
          data-testid="mobile-menu"
          style={{ background: "#fff", borderTop: "1px solid var(--line)" }}
        >
          <div className="container-x py-6 flex flex-col gap-1">
            <Link
              to="/"
              className="py-3 text-lg font-display"
              style={{ borderBottom: "1px solid var(--line)", color: "var(--ink)" }}
            >
              Accueil
            </Link>
            <Link
              to="/nos-locations"
              className="py-3 text-lg font-display"
              style={{ borderBottom: "1px solid var(--line)", color: "var(--ink)" }}
              data-testid="mobile-nav-locations"
            >
              Nos locations
            </Link>
            {[
              { label: "Ménage", to: "/menage" },
              { label: "Conciergerie", to: "/conciergerie" },
              { label: "Notre histoire", to: "/a-propos" },
              { label: "FAQ", to: "/a-propos/faq-generale" },
              { label: "Contact", to: "/contact" },
            ].map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className="py-3 text-lg font-display"
                style={{ borderBottom: "1px solid var(--line)", color: "var(--ink)" }}
              >
                {l.label}
              </Link>
            ))}
            <Link to="/contact" className="btn btn-primary mt-5 justify-center">
              Demander un devis
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}

function Dropdown({ label, links, open, onEnter, onLeave, testid }) {
  return (
    <div
      className="relative"
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
    >
      <button
        className="nav-link flex items-center gap-1"
        data-testid={testid}
        style={{ color: "inherit" }}
      >
        {label}
        <ChevronDown size={14} />
      </button>
      {open && (
        <div
          className="dropdown-panel absolute left-0 top-full pt-4"
          style={{ minWidth: 230 }}
        >
          <div
            style={{
              background: "#fff",
              border: "1px solid var(--line)",
              boxShadow: "0 25px 50px -20px rgba(12,31,39,0.4)",
            }}
          >
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className="block px-5 py-3.5 text-sm transition-colors"
                style={{ color: "var(--ink)" }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "var(--cream)")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
              >
                {l.label}
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
